import { useEffect, useState } from 'react';
import { T } from '../lib/strings.ts';
import { cn } from '../lib/utils.ts';
import { IconMonitor, IconMoon, IconSun } from './brand/icons.tsx';

// ערכת צבע (11 §4): ברירת מחדל לפי המערכת; בחירה ידנית → [data-theme] על <html>.
// נשמר ב-localStorage כנוחות בלבד (11 §2 "ui"), לא כאמת.
type Theme = 'system' | 'light' | 'dark';
const KEY = 'aibify.ui.theme';

function read(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}
function apply(t: Theme) {
  if (t === 'system') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = t;
}
apply(read());

const NEXT: Record<Theme, Theme> = { system: 'dark', dark: 'light', light: 'system' };
const ICON = { system: IconMonitor, dark: IconMoon, light: IconSun } as const;

/** הבחירה הנוכחית ומעבר לבאה (system → dark → light); משמש גם את פריט "ערכת צבע" בתפריט המשתמש (web-instance-identity). */
export function useTheme() {
  const [t, setT] = useState<Theme>(read);
  useEffect(() => {
    apply(t);
    try {
      if (t === 'system') localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, t);
    } catch {}
  }, [t]);
  return { theme: t, next: () => setT(NEXT[t]), Icon: ICON[t], label: `${T.theme.label}: ${T.theme[t]}` };
}

/** label — תווית גלויה מתחת לאייקון. */
export function ThemeToggle({ className = '', label = false }: { className?: string; label?: boolean }) {
  const { theme: t, next, Icon } = useTheme();
  return (
    <button
      type="button"
      data-testid="theme-toggle"
      data-theme-choice={t}
      onClick={next}
      title={`${T.theme.label}: ${T.theme[t]}`}
      aria-label={`${T.theme.label}: ${T.theme[t]}`}
      className={cn('inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground', className)}
    >
      <Icon size={20} />
      {label && <span aria-hidden="true">{T.theme.label}</span>}
    </button>
  );
}
